// @flow
import * as React from 'react';
import type { FormProps } from 'redux-form';

import clsx from 'clsx';

import { Alert, Base, Button, Svg, Text, Label } from '@gandi/ui';

import { Form, FormField } from 'gandi.react-form';

import type { TranslatorProps } from '@gandi/translate';
import type { WithRouterProps } from 'src/helpers/withRouter';

import type { Subject } from '../../../../Support/Contact/types';
import { SimpleSelect } from '../../../../SelectComponent';
import styles from './AbuseForm.scss';
import Message from './Message';
import UserInfoFields from './UserInfoFields';
import DomainFields from './DomainFields';
import HostingFields from './HostingFields';

export type StateProps = {|
  disabledTips: Array<string>,
  disabledWarnings: Array<string>,
  submitErrors?: {|
    resource_name: string,
  |},
|};

export type OwnProps = {|
  onSubmit: (value: { ... }) => Promise<mixed>,
  routeParams: {| category?: string, topic?: string, subject?: Subject |},
  reduxFormProps: FormProps,
|};

export type InjectedProps = {|
  translator: TranslatorProps,
  ...WithRouterProps<{ ... }>,
|};

export type Props = {| ...InjectedProps, ...OwnProps, ...StateProps |};
const variant = ['small', 'block'];

function AbuseForm(props: Props): React.Element<typeof Form> {
  // Data select, tips and warning
  const categories = [
    { text: props.translator.__('Domain'), value: 'domain' },
    { text: props.translator.__('Hosting'), value: 'hosting' },
  ];

  const warnings = {
    whois: [
      {
        type: 'warning',
        message: props.translator.__(
          'GANDI will automatically forward some of the information that you provide' +
            ' to the parties involved, so that they can take corrective action rapidly.'
        ),
      },
    ],
    spam: [
      {
        type: 'warning',
        message: props.translator.__(
          'GANDI will automatically forward some of the information that you provide' +
            ' to the parties involved, so that they can take corrective action rapidly.'
        ),
      },
    ],
    breaching: [
      {
        type: 'warning',
        message: props.translator.__(
          'GANDI will automatically forward some of the information that you provide' +
            ' to the parties involved, so that they can take corrective action rapidly.'
        ),
      },
    ],
    malware: [
      {
        type: 'warning',
        message: props.translator.__(
          'GANDI will automatically forward some of the information that you provide' +
            ' to the parties involved, so that they can take corrective action rapidly.'
        ),
      },
    ],
    pirated: [
      {
        type: 'warning',
        message: props.translator.__(
          'GANDI will automatically forward some of the information that you provide' +
            ' to the parties involved, so that they can take corrective action rapidly.'
        ),
      },
    ],
  };

  const tips = {
    whois: [
      {
        type: 'info',
        message: props.translator.__(
          'In the framework of a complaint for incorrect WHOIS information, ' +
            'and in conformity with the Registrar Accreditation Agreement (RAA) ' +
            'that binds us to ICANN, our agents will ask that the owners of possibly ' +
            'litigious domain names provide us with documentation' +
            ' that proves the accuracy of the WHOIS data they provided, ' +
            'and this, within the deadlines and specifications ' +
            "of ICANN's WHOIS data accuracy program."
        ),
      },
    ],
    cybersquatting: [
      {
        type: 'info',
        message: props.translator.__(
          'For any court order or arbitral award (UDRP award or any decision ' +
            'by an arbitral authority designated by the registries to handle alternative ' +
            'dispute resolution procedures), Gandi will execute the order to the best of its ' +
            'ability within the required deadline (10 workdays starting from the notification ' +
            'of the decision of the arbitral authority within the framework of a UDRP decision, ' +
            'in application of rule 4k of the guiding principles of the uniform domain name ' +
            'resolution procedures). Gandi will handle each request that emanates from a ' +
            'governmental or quasi-governmental (Independent Administrative Authority,...) ' +
            'within the framework of applicable law.'
        ),
      },
    ],
    illegal: [
      {
        type: 'info',
        message: props.translator.__(
          'If the wrong category is chosen for your complaint, or if it is misused, ' +
            'your complaint may not be handled.' +
            ' Additionally, the act of flagging content or activity as being illicit ' +
            'to a web host with the goal of obtaining the withdrawal of the content ' +
            'or its diffusion, when the author of the complaint knows that the ' +
            'information is false, is punishable up to one year in prison ' +
            'and a fine of 15,000 euros.'
        ),
      },
    ],
    breaching: [
      {
        type: 'info',
        message: props.translator.__(
          'If the wrong category is chosen for your complaint, or if it is misused,' +
            ' your complaint may not be handled.' +
            ' Additionally, the act of flagging content or activity as being illicit ' +
            'to a web host with the goal of obtaining the withdrawal of ' +
            'the content or its diffusion, when the author of the complaint knows ' +
            'that the information is false, is punishable up to one year in prison ' +
            'and a fine of 15,000 euros.'
        ),
      },
    ],
  };

  const topics = {
    domain: [
      {
        text: props.translator.__('Incorrect WHOIS information'),
        value: 'whois',
      },
      { text: props.translator.__('Spam'), value: 'spam' },
      { text: props.translator.__('Phishing'), value: 'phishing' },
      {
        text: props.translator.__('Cybersquatting'),
        value: 'cybersquatting',
      },
      { text: props.translator.__('Other'), value: 'other' },
    ],
    hosting: [
      {
        text: props.translator.__('Spam'),
        value: 'spam',
      },
      {
        text: props.translator.__(
          'Illegal content (terrorism, child pornography, etc.)'
        ),
        value: 'illegal',
      },
      {
        text: props.translator.__(
          'Content that infringes on third-party rights'
        ),
        value: 'breaching',
      },
      {
        text: props.translator.__('Malware - virus'),
        value: 'malware',
      },
      {
        text: props.translator.__('Computer attack'),
        value: 'pirated',
      },
      {
        text: props.translator.__('Other'),
        value: 'other',
      },
    ],
  };

  const onChangeCategory = (evt: SyntheticEvent<HTMLInputElement>) => {
    props.router.push({
      pathname: `/abuse/${evt.currentTarget.value}`,
      state: { noTrack: true },
    });
  };

  const onChangeTopic = (evt: SyntheticEvent<HTMLInputElement>) => {
    props.router.push(
      // $FlowFixMe[incompatible-type] Always defined here
      `/abuse/${props.routeParams.category}/${evt.currentTarget.value}`
    );
  };

  const renderTips = (topic: string) => {
    const message = tips[topic][0].message;
    return (
      <Base backgroundColor="grey-lighter" padding="small" separatorBottom>
        <Text key="tips">{message}</Text>
      </Base>
    );
  };

  const renderWarning = (topic: string) => {
    const { __ } = props.translator;
    const message = warnings[topic][0].message;
    return (
      <Base separatorTop key="tips-warning">
        <Alert
          className={styles.alert}
          message={message}
          title={__('Warning')}
          intent="warning"
        />
      </Base>
    );
  };

  const {
    translator,
    disabledTips,
    disabledWarnings,
    onSubmit,
    reduxFormProps: { error, handleSubmit },
    routeParams: { category, topic },
    submitErrors,
  } = props;

  const { __ } = translator;
  const showHostingFields = category === 'hosting';
  const showDomainFields = category === 'domain';

  return (
    <Form
      id="abuse-support-form"
      method="post"
      onSubmit={handleSubmit(onSubmit)}
      className={clsx(styles.form, 'form-stacked')}
    >
      {!!category && (!!topic || !topics[category]) && !!error && (
        <Base marginBottom="small">
          <Alert
            message={(submitErrors && submitErrors.resource_name) || error}
            intent="danger"
          />
        </Base>
      )}
      <Base>
        <Base
          flex
          flexWrap
          backgroundColor="grey-lighter"
          padding="small"
          separatorBottom
        >
          {/* Select category */}
          <Base className="for-2-columns">
            <Text tagName="label" htmlFor="category">
              {__('Object of your complaint')}
            </Text>
            <Base flex>
              <FormField
                component={SimpleSelect}
                id="category"
                name="category"
                onChange={onChangeCategory}
                placeholder={__('Choose a product')}
                values={categories.filter(option => option.value)}
                variant={variant}
                // this prop is used when a user share an url
                defaultValue={category}
              />
              {!category ? (
                <Button
                  className={styles.nojsbutton}
                  name="choose_subject"
                  small
                  type="submit"
                >
                  {__('Ok')}
                </Button>
              ) : (
                <Svg
                  className={styles.modeSvg}
                  name="checkmark"
                  theme="success"
                />
              )}
            </Base>
          </Base>
          {/* Select Topics */}
          <div
            className={clsx(
              'for-2-columns',
              !category || !topics[category] ? styles.disabled : null
            )}
          >
            <Label target="sub_category">{__('Category')}</Label>
            <Base flex>
              <FormField
                component={SimpleSelect}
                disabled={!category || !topics[category]}
                id="sub_category"
                name="sub_category"
                onChange={onChangeTopic}
                placeholder={__('Select topic')}
                values={(category && topics[category]) || []}
                variant={variant}
                // this prop is used when a user share an url
                defaultValue={topic}
              />
              {!category && (
                <Button
                  className={styles.nojsbuttonHidden}
                  disabled
                  small
                  aria-hidden="true"
                  tabIndex="-1"
                >
                  {__('Ok')}
                </Button>
              )}
              {category &&
                (!topic ? (
                  <Button
                    className={styles.nojsbutton}
                    name="choose_topic"
                    small
                    type="submit"
                  >
                    {__('Ok')}
                  </Button>
                ) : (
                  <Svg
                    className={styles.modeSvg}
                    name="checkmark"
                    theme="success"
                  />
                ))}
            </Base>
          </div>
        </Base>

        {!!topic &&
          !disabledTips.includes(category) &&
          Object.keys(tips).includes(topic) &&
          renderTips(topic)}

        <Base>
          {!!category && (!!topic || !topics[category]) && (
            <>
              <UserInfoFields showHostingFields topic={topic} />

              {showDomainFields && (
                <DomainFields category={category} topic={topic} />
              )}

              {showHostingFields && (
                <HostingFields category={category} topic={topic} />
              )}

              <Message topics={topics} category={category} topic={topic} />
            </>
          )}
        </Base>

        {!!category &&
          (!!topic || !topics[category]) &&
          topic !== 'breaching' &&
          !disabledWarnings.includes(category) &&
          Object.keys(warnings).includes(topic) &&
          topic &&
          renderWarning(topic)}

        {!!category && (!!topic || !topics[category]) ? (
          <Base flex flexJustifyEnd marginTop="small" marginBottom="medium">
            <Button type="submit" grouped>
              {__('Send')}
            </Button>
          </Base>
        ) : null}
      </Base>
    </Form>
  );
}

export default AbuseForm;
